<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const scrolled = ref(false)
const mobileMenuOpen = ref(false)
const loginMenuOpen = ref(false)
const loginMenuEl = ref(null)

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://app.krate.sh'
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.krate.sh'

const loginGitHubUrl = `${API_BASE}/api/auth/github`
const loginGoogleUrl = `${API_BASE}/api/auth/google`
// Note: no separate signupUrl. New users use the same "Log in with X"
// flow — the auth-server's signup-gate detects unrecognised emails and
// bounces to the marketing site with ?signup=required so they can apply.
// "Join the beta" is the alternative for users who'd rather submit
// their email up front without an OAuth round-trip.

const handleScroll = () => {
  scrolled.value = window.scrollY > 50
}
const handleDocumentClick = (e) => {
  if (loginMenuEl.value && !loginMenuEl.value.contains(e.target)) {
    loginMenuOpen.value = false
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', handleScroll)
    document.addEventListener('click', handleDocumentClick)
  }
})
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('click', handleDocumentClick)
  }
})

const goToWaitlist = () => {
  if (router.currentRoute.value.name !== 'home') {
    router.push({ name: 'home', hash: '#waitlist' })
  } else {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
  }
  mobileMenuOpen.value = false
}
</script>

<template>
  <nav
    :class="[
      'sticky top-0 z-50 transition-all duration-200',
      scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'
    ]"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
      <router-link to="/" class="flex items-center gap-3 text-3xl font-bold tracking-tight" style="color: var(--color-krate-blue-600);">
        <img src="/img/logo.svg" alt="" width="50" height="50" class="block" />
        <span>krate</span>
      </router-link>

      <div class="hidden md:flex items-center gap-8">
        <router-link to="/pricing" class="text-sm font-medium hover:text-blue-600 transition-colors" style="color: var(--color-krate-dark-700);">
          Pricing
        </router-link>
        <router-link to="/roadmap" class="text-sm font-medium hover:text-blue-600 transition-colors" style="color: var(--color-krate-dark-700);">
          Roadmap
        </router-link>
        <a href="https://github.com/krate-dot-sh/krate-docs" class="text-sm font-medium hover:text-blue-600 transition-colors" style="color: var(--color-krate-dark-700);">Docs</a>
        <a href="https://github.com/krate-dot-sh" class="text-sm font-medium hover:text-blue-600 transition-colors" style="color: var(--color-krate-dark-700);">GitHub</a>

        <div ref="loginMenuEl" class="relative">
          <button
            @click="loginMenuOpen = !loginMenuOpen"
            class="text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
            style="background-color: var(--color-krate-blue-600); color: white;"
            :aria-expanded="loginMenuOpen"
            aria-haspopup="true"
          >
            Sign in
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" :class="{ 'rotate-180': loginMenuOpen }" style="transition: transform 0.15s;">
              <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
          <div
            v-if="loginMenuOpen"
            class="absolute right-0 top-full mt-2 min-w-[240px] bg-white border rounded-lg shadow-xl p-2 z-50"
            style="border-color: #e2e8f0;"
          >
            <a :href="loginGitHubUrl" data-umami-event="signin-github" data-umami-event-location="nav" class="idp-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
              <span>Sign in with GitHub</span>
            </a>

            <a :href="loginGoogleUrl" data-umami-event="signin-google" data-umami-event-location="nav" class="idp-item">
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Sign in with Google</span>
            </a>

          </div>
        </div>

        <button @click="goToWaitlist" data-umami-event="cta-nav-beta" class="beta-btn">
          Join the beta
        </button>
      </div>

      <button class="md:hidden p-2" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Toggle menu">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'" />
        </svg>
      </button>
    </div>

    <div v-if="mobileMenuOpen" class="md:hidden border-t bg-white px-4 py-4 flex flex-col gap-3">
      <router-link to="/pricing" class="text-sm font-medium" @click="mobileMenuOpen = false">Pricing</router-link>
      <router-link to="/roadmap" class="text-sm font-medium" @click="mobileMenuOpen = false">Roadmap</router-link>
      <a href="https://github.com/krate-dot-sh/krate-docs" class="text-sm font-medium">Docs</a>
      <a href="https://github.com/krate-dot-sh" class="text-sm font-medium">GitHub</a>
      <div class="border-t pt-3 mt-1 flex flex-col gap-2.5">
        <a :href="loginGitHubUrl" class="idp-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
          <span>Sign in with GitHub</span>
        </a>
        <a :href="loginGoogleUrl" class="idp-item">
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span>Sign in with Google</span>
        </a>
        <button @click="goToWaitlist" class="beta-btn self-start">
          Join the beta
        </button>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.idp-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.8rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  background: #fff;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}
.idp-item:hover {
  background: var(--color-krate-blue-600);
  color: #fff;
  border-color: var(--color-krate-blue-600);
}
.idp-item svg { flex-shrink: 0; }
.idp-item span { flex: 1; text-align: center; }
.beta-btn {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;
}
.beta-btn:hover {
  background: #f59e0b;
  color: #fff;
  border-color: #f59e0b;
}
</style>
